import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import customerDataManager, { getCustomerStats } from '../../utils/customerDataManager';

const CustomerDashboard = () => {
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [stats, setStats] = useState({});
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    loadCustomerData();
    loadNotifications();
  }, []);

  useEffect(() => {
    filterCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers, selectedStatus, searchTerm]);

  const loadCustomerData = () => {
    const allCustomers = customerDataManager.getAllCustomers();
    const customerStats = getCustomerStats();
    setCustomers(allCustomers);
    setStats(customerStats);
  };

  const loadNotifications = () => {
    const notifs = JSON.parse(localStorage.getItem('makrana_notifications') || '[]');
    setNotifications(notifs.filter(n => n.status === 'pending'));
  };

  const filterCustomers = () => {
    let filtered = customers;

    // Filter by status
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(customer => customer.status === selectedStatus);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(customer => 
        customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.mobile.includes(searchTerm) ||
        customer.inquiryType.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredCustomers(filtered);
  };

  const updateCustomerStatus = (customerId, newStatus) => {
    const success = customerDataManager.updateCustomerStatus(customerId, newStatus);
    if (success) {
      loadCustomerData();
      setSelectedCustomer(null);
    }
  };

  const exportCustomers = () => {
    customerDataManager.exportCustomersCSV();
  };

  const markNotificationsRead = () => {
    customerDataManager.markNotificationsRead();
    setNotifications([]);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const parseProjectDetails = (detailsString) => {
    try {
      return JSON.parse(detailsString);
    } catch {
      return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-800';
      case 'contacted': return 'bg-yellow-100 text-yellow-800';
      case 'converted': return 'bg-green-100 text-green-800';
      case 'lost': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6 bg-gray-50 min-h-screen">
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-gray-900">📊 Customer Dashboard</h1>
          <div className="flex gap-4">
            {notifications.length > 0 && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                onClick={markNotificationsRead}
                className="bg-red-500 text-white px-4 py-2 rounded-lg flex items-center gap-2"
              >
                🔔 {notifications.length} New Notifications
              </motion.button>
            )}
            <motion.button
              whileHover={{ scale: 1.05 }}
              onClick={exportCustomers}
              className="bg-green-600 text-white px-4 py-2 rounded-lg"
            >
              📁 Export CSV
            </motion.button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-xl shadow-lg">
            <h3 className="text-lg font-semibold text-gray-700">Total Customers</h3>
            <p className="text-3xl font-bold text-blue-600">{stats.total || 0}</p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-lg">
            <h3 className="text-lg font-semibold text-gray-700">Today</h3>
            <p className="text-3xl font-bold text-green-600">{stats.today || 0}</p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-lg">
            <h3 className="text-lg font-semibold text-gray-700">This Week</h3>
            <p className="text-3xl font-bold text-purple-600">{stats.thisWeek || 0}</p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-lg">
            <h3 className="text-lg font-semibold text-gray-700">New Inquiries</h3>
            <p className="text-3xl font-bold text-orange-600">{stats.new || 0}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white p-6 rounded-xl shadow-lg mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search by name, mobile, or inquiry type..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="converted">Converted</option>
              <option value="lost">Lost</option>
            </select>
          </div>
        </div>
      </div>

      {/* Customer List */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Inquiry Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Budget</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredCustomers.map((customer) => (
                <motion.tr
                  key={customer.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => setSelectedCustomer(customer)}
                >
                  <td className="px-6 py-4">
                    <div>
                      <div className="font-medium text-gray-900">{customer.name}</div>
                      <div className="text-sm text-gray-500">{customer.id}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{customer.mobile}</div>
                    <a 
                      href={`https://wa.me/91${customer.mobile}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-green-600 hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      📱 WhatsApp
                    </a>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 capitalize">
                    {customer.inquiryType.replace('_', ' ')}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {formatDate(customer.timestamp)}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(customer.status)}`}>
                      {customer.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {customer.estimatedBudget ? `₹${customer.estimatedBudget.toLocaleString()}` : '-'}
                  </td>
                  <td className="px-6 py-4">
                    <select
                      value={customer.status}
                      onChange={(e) => {
                        e.stopPropagation();
                        updateCustomerStatus(customer.id, e.target.value);
                      }}
                      className="text-sm border border-gray-300 rounded px-2 py-1"
                    >
                      <option value="new">New</option>
                      <option value="contacted">Contacted</option>
                      <option value="converted">Converted</option>
                      <option value="lost">Lost</option>
                    </select>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Customer Detail Modal */}
      {selectedCustomer && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedCustomer(null)}
        >
          <motion.div
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Customer Details</h2>
                <button
                  onClick={() => setSelectedCustomer(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Name</label>
                    <p className="text-lg font-semibold">{selectedCustomer.name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Mobile</label>
                    <p className="text-lg">{selectedCustomer.mobile}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Inquiry Type</label>
                    <p className="text-lg capitalize">{selectedCustomer.inquiryType.replace('_', ' ')}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Date</label>
                    <p className="text-lg">{formatDate(selectedCustomer.timestamp)}</p>
                  </div>
                </div>

                {selectedCustomer.projectDetails && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Project Details</label>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      {(() => {
                        const details = parseProjectDetails(selectedCustomer.projectDetails);
                        if (details) {
                          return (
                            <div className="space-y-2">
                              <p><strong>Type:</strong> {details.type}</p>
                              <p><strong>Marble:</strong> {details.marbleType}</p>
                              <p><strong>Grade:</strong> {details.grade}</p>
                              <p><strong>Thickness:</strong> {details.thickness}</p>
                              <p><strong>Area:</strong> {details.area} sq ft</p>
                              <p><strong>Location:</strong> {details.location}</p>
                              <p><strong>Total Cost:</strong> ₹{details.totalCost?.toLocaleString()}</p>
                              <p><strong>Timeline:</strong> {details.timeline} days</p>
                            </div>
                          );
                        }
                        return <p>{selectedCustomer.projectDetails}</p>;
                      })()}
                    </div>
                  </div>
                )}

                <div className="flex gap-4">
                  <a
                    href={`tel:+91${selectedCustomer.mobile}`}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
                  >
                    📞 Call
                  </a>
                  <a
                    href={`https://wa.me/91${selectedCustomer.mobile}?text=Hi ${selectedCustomer.name}, this is from Makrana Marble. Thank you for your inquiry!`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2"
                  >
                    💬 WhatsApp
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default CustomerDashboard;